import React from "react";
import { Input, Select, StatusPill } from "@/ds";
import type { RuleParameterDefinition, RuleValueGrant } from "@/api";
import { useT } from "../i18n";
import { ruleValueNumber } from "./rule-parameter-display";

export function BusinessRuleValueField({ definition, value, grant, readOnly, busy, errors, invariants, onChange }: {
  definition: RuleParameterDefinition; value: string | boolean; grant?: RuleValueGrant;
  readOnly: boolean; busy: boolean; errors: string[]; invariants: string[];
  onChange: (value: string | boolean) => void;
}) {
  const t = useT();
  const rangeGrant = grant?.editable ? grant : undefined;
  const editable = !readOnly && !!rangeGrant && (definition.value_type === "boolean"
    || [rangeGrant.min, rangeGrant.max, rangeGrant.step].every(Number.isSafeInteger));
  const id = `rule-param-${definition.key}`;
  const label = t(`businesses.rules.param.${definition.key}`, undefined, definition.key);
  const shown = typeof value === "boolean" ? t(value ? "businesses.rules.yes" : "businesses.rules.no") : value;
  const range = grant?.editable && definition.value_type === "integer"
    ? t("businesses.rules.delegation.range", {
      min: ruleValueNumber(grant.min!, definition.unit), max: ruleValueNumber(grant.max!, definition.unit),
      step: ruleValueNumber(grant.step!, definition.unit),
    }) : null;
  return <div className={`reqventos-business-rule ${editable ? "" : "reqventos-business-rule--readonly"}`}
    data-testid={id}>
    <StatusPill tone={grant?.editable ? "info" : "neutral"}
      label={t(grant?.editable ? "ruleValues.editable" : "businesses.rules.plane.platform")} />
    <div className="reqventos-business-rule__sentence">
      <label htmlFor={editable ? id : undefined}>{t(`ruleValues.sentence.${definition.key}.before`)}</label>{" "}
      {!editable ? <strong className="reqventos-business-rule__value">{shown}</strong>
        : definition.value_type === "boolean" ? <Select id={id} value={String(value)} disabled={busy}
          aria-label={label} error={errors.length > 0} aria-invalid={errors.length > 0} aria-describedby={`${id}-hint${errors.length ? ` ${id}-error` : ""}`}
          options={[{ value: "true", label: t("businesses.rules.yes") }, { value: "false", label: t("businesses.rules.no") }]}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value === "true")} />
          : <Input id={id} type="number" value={String(value)} disabled={busy}
            inputMode={definition.unit === "eur_cents" ? "decimal" : "numeric"} aria-label={label}
            min={ruleValueNumber(rangeGrant!.min!, definition.unit)}
            max={ruleValueNumber(rangeGrant!.max!, definition.unit)}
            step={ruleValueNumber(rangeGrant!.step!, definition.unit)}
            error={errors.length > 0} aria-invalid={errors.length > 0} aria-describedby={`${id}-hint${errors.length ? ` ${id}-error` : ""}`}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)} />}{" "}
      <span>{t(`ruleValues.sentence.${definition.key}.after`)}</span>
    </div>
    <div id={`${id}-hint`} className="subtle stack-xs">
      {range && <span>{range}{definition.unit === "eur_cents" ? " EUR" : ""}</span>}
      {invariants.map((hint) => <span key={hint}>{hint}</span>)}
    </div>
    {errors.length > 0 && <p id={`${id}-error`} className="reqventos-business-rule__error" role="alert">{errors.join(" ")}</p>}
  </div>;
}
